import { Separator } from 'shared/ui/separator';
import type { SetStateAction, Dispatch } from 'react';
import { useCallback, useMemo } from 'react';
import { File, X } from 'lucide-react';
import { Button } from 'shared/ui/button';
import { useMutation } from '@tanstack/react-query';
import { LoadingButton } from 'shared/ui/loading-button';
import { useApiStatusToast } from 'shared/hooks/useApiStatusToast';
import { deleteCodeFiles } from '@/enteties/file-storage/api/git';
import * as m from '@paraglide/messages';
export interface UploadedFile {
  path: string | undefined;
  name: string;
}
interface UploadedFilesProps {
  uploadedFiles: UploadedFile[];
  setUploadedFiles: Dispatch<SetStateAction<UploadedFile[]>>;
  slug: string;
}
export const UploadedFiles = ({
  uploadedFiles,
  setUploadedFiles,
  slug
}: UploadedFilesProps) => {
  const [_, error] = useApiStatusToast();
  const {
    mutate: deleteFile,
    isPending
  } = useMutation({
    mutationFn: ({
      path: pathName
    }: {
      path: string[];
    }) => deleteCodeFiles(slug, {
      commitMsg: `deleted ${pathName}`,
      branch: 'master',
      email: 'git@amvera.ru',
      filePaths: pathName
    }),
    onSuccess: (_, variables) => {
      setUploadedFiles(prev => prev.filter(file => !variables.path.includes(file.path)));
    },
    onError: e => error({
      description: e.message
    })
  });
  const handleSingleDelete = useCallback((filePath: string) => {
    deleteFile({
      path: [filePath]
    });
  }, [deleteFile]);
  const handleDeleteAll = useCallback(() => {
    deleteFile({
      path: uploadedFiles.map(file => file.path) as string[]
    });
  }, [deleteFile, uploadedFiles]);
  const uploadedFilesList = useMemo(() => uploadedFiles.map(file => <div className="grid grid-cols-[1fr_auto] w-full gap-2 items-center" key={file.path}>
          <div className="flex items-center min-w-0 overflow-hidden">
            <File size={16} className="shrink-0 mr-2" />
            <span className="truncate text-title4 font-normal">
              {file.path}
            </span>
          </div>
          <LoadingButton type="button" loading={isPending} variant="none" size="sm" className="shrink-0" onClick={() => handleSingleDelete(file.path as string)}>
            <X size={16} />
          </LoadingButton>
        </div>), [handleSingleDelete, isPending, uploadedFiles]);
  return <div className="border rounded-xl py-4 px-6 bg-card w-full" data-sentry-component="UploadedFiles" data-sentry-source-file="uploaded-files.tsx">
      <div className="mb-3 grid grid-cols-[1fr_auto] items-center w-full">
        <h1>{m.upload_dialog_field_upload_uploadedFiles()}</h1>
        {!!uploadedFiles.length && <Button className="text-status-tertiary p-0 h-fit" variant="link" size="sm" onClick={handleDeleteAll} type="button">
            {m.upload_dialog_field_upload_deleteAll()}
          </Button>}
      </div>
      <Separator variant="dark" data-sentry-element="Separator" data-sentry-source-file="uploaded-files.tsx" />
      {uploadedFiles.length ? <div className="max-h-52 overflow-y-auto w-full">
          <div className="flex flex-col gap-2 w-full py-2">
            {uploadedFilesList}
          </div>
        </div> : <p className="text-title4 font-normal text-foreground/50 mt-3">
          {m.upload_dialog_field_upload_noFiles()}
        </p>}
    </div>;
};